import { useLocation, Navigate } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/components/Seo';
import { LEGAL_PAGES } from '@/data/site';
import { LEGAL_PAGES_EN } from '@/data/en/site';
import { useLang } from '@/i18n/LangContext';
import { toItPath } from '@/i18n/slugs';

export default function LegalPage() {
  const { pathname } = useLocation();
  const { lang, t } = useLang();
  const cleanPath = lang === 'en'
    ? toItPath(pathname.replace(/^\/en(?=\/|$)/, '') || '/')
    : pathname;
  const registry = lang === 'en' ? LEGAL_PAGES_EN : LEGAL_PAGES;
  const page = registry[cleanPath];
  const description = page?.intro || page?.paragraphs?.[0] || '';

  useSeo({ title: page ? page.title : t('notFoundTitle'), description });
  if (!page) return <Navigate to={lang === 'en' ? '/en/404' : '/404'} replace />;

  return (
    <div data-testid={`legal-page-${cleanPath.replace(/\//g, '-')}`}>
      <PageHero
        eyebrow={lang === 'en' ? 'Legal information' : 'Informazioni legali'}
        title={page.title}
        updated={page.updated}
        breadcrumbs={[{ label: t('breadcrumbHome'), href: '/' }, { label: page.title }]}
      />
      <div className="mx-auto max-w-site px-5 md:px-8 py-14 md:py-20">
        <Reveal className="max-w-prose guide-body">
          {page.intro && <p className="text-lg leading-relaxed text-foreground/80">{page.intro}</p>}

          {page.sections?.map((section) => (
            <section key={section.title} className="mt-10 first:mt-8">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground mb-4">{section.title}</h2>
              {section.paragraphs?.map((paragraph, index) => (
                <p key={`${section.title}-p-${index}`}>{paragraph}</p>
              ))}
              {section.items?.length > 0 && (
                <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-primary">
                  {section.items.map((item, index) => (
                    <li key={`${section.title}-item-${index}`}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {!page.sections && page.paragraphs?.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}

          {page.resources?.length > 0 && (
            <div className="mt-10 border-t border-border pt-7">
              <h2 className="font-heading text-xl font-bold text-foreground mb-3">
                {lang === 'en' ? 'Further information' : 'Approfondimenti'}
              </h2>
              <ul className="space-y-2">
                {page.resources.map((resource) => (
                  <li key={resource.href}>
                    <a
                      href={resource.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-primary underline underline-offset-4 hover:text-brand-dark"
                    >
                      {resource.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {page.finmaRegistration && (
            <p className="mt-6">
              <span className="font-semibold text-foreground">{page.finmaRegistration.label}: </span>
              <a
                href={page.finmaRegistration.href}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline underline-offset-4 hover:text-brand-dark"
              >
                {page.finmaRegistration.number}
              </a>
            </p>
          )}
        </Reveal>
      </div>
    </div>
  );
}
